import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGetUsersInfiniteQuery } from 'src/apiClients';
import type { QueryWhereValue, UserListItem } from 'src/apiClients/queries';
import { EntityState, USER_SEARCH_COLUMNS, USER_UUID_COLUMNS } from 'src/apiClients/queries';
import { SortDirection } from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';
import { i18n as routesI18n } from 'src/hooks/use-imago-mp-routes.i18n';
import { useSharedEntityFilters } from 'src/hooks/use-shared-entity-filters';
import { createUserColumns, mapUserRow } from 'src/pages/users-page/user-table-formatters';
import { userTableI18n } from 'src/pages/users-page/user-table-formatters.i18n';

import { useInstance } from '../../InstanceContext';
import { SectionHeading } from '../SectionHeading';
import { i18n } from './UsersSection.i18n';

// `login`/`email`/`name` are text columns matched with `$iLike`. `id` and
// `seequentUserId` are UUID columns that can't use `$iLike` (Postgres type
// mismatch); they're matched exactly via `$eq` when the search term is a full UUID.
const SEARCH_COLUMNS = USER_SEARCH_COLUMNS;
const UUID_SEARCH_COLUMNS = USER_UUID_COLUMNS;
const INITIAL_SORT = [{ field: 'state', direction: SortDirection.ASC }];
const INITIAL_FILTERS = { state: [EntityState.ACTIVE, EntityState.INACTIVE] };

export const UsersSection = () => {
    const intl = useIntl();
    const { subscription } = useInstance();
    const [total, setTotal] = useState<number | undefined>(undefined);
    const [filters, setFilters] = useSharedEntityFilters('user', INITIAL_FILTERS);

    const baseWhere = useMemo<Record<string, QueryWhereValue>>(
        () => ({ subscriptionId: subscription.id }),
        [subscription.id],
    );

    const columns = useMemo(() => createUserColumns(intl), [intl]);

    const handleTotalChange = useCallback((next: number) => setTotal(next), []);

    return (
        <>
            <SectionHeading
                title={intl.formatMessage(routesI18n.instanceSectionUsers)}
                count={total}
                automationId="instance-users-count"
            />
            <EntityListPage<UserListItem>
                automationId="instance-users-section"
                filterGroupLabel={intl.formatMessage(routesI18n.instanceSectionUsers)}
                useQuery={useGetUsersInfiniteQuery}
                searchColumns={SEARCH_COLUMNS}
                uuidSearchColumns={UUID_SEARCH_COLUMNS}
                initialSort={INITIAL_SORT}
                columns={columns}
                mapRow={mapUserRow}
                baseWhere={baseWhere}
                onTotalChange={handleTotalChange}
                initialFilters={filters}
                onFiltersChange={setFilters}
                titleWhenNoResults={intl.formatMessage(userTableI18n.noUsersTitle)}
                messageWhenNoResults={intl.formatMessage(i18n.noUsersMessage)}
                titleWhenLoadError={intl.formatMessage(userTableI18n.loadErrorTitle)}
                messageWhenLoadError={intl.formatMessage(userTableI18n.loadErrorMessage)}
            />
        </>
    );
};
